import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, Link } from '../../app/router';
import { canDelete, type LifecycleAction, type Org, type StorageRow } from '../../lib/api';
import { Button, Checkbox, Dialog, Input, Select } from '../../ui';
import { cn } from '../../ui/cn';
import { focusRing } from '../../ui/focus';
import { useSession } from '../auth/session';
import { relativeTime, Section } from '../dashboard/cards';
import { settle } from '../dashboard/bulk';
import type { Inverse } from '../dashboard/model/actions';
import { qk, useInbox, useInvalidateContent } from '../dashboard/queries';
import { useUndoable } from '../dashboard/undo';
import { formatBytes } from './format';
import { LightenDialog } from './LightenDialog';

type Show = 'all' | 'active' | 'archived' | 'trash';
type SortKey = 'total' | 'title' | 'project' | 'edited';

const total = (r: StorageRow) => r.docBytes + r.imageBytes + r.versionBytes;
const shows = (show: Show, r: StorageRow) =>
  show === 'trash' ? Boolean(r.deletedAt) : !r.deletedAt && (show === 'all' || (show === 'archived') === Boolean(r.archivedAt));

const INVERSE: Partial<Record<LifecycleAction, LifecycleAction>> = { archive: 'unarchive', unarchive: 'archive', trash: 'restore', restore: 'trash' };

/** Images stored against the quota (what `org_storage` measures); uploads stop at 80 % (spec §I-P4). */
function Gauge({ org }: { org: Org }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const storage = useQuery({ queryKey: ['storage', org.id], queryFn: () => api.orgStorage(org.id) });
  if (!storage.data) return null;
  const { usedBytes, quotaBytes } = storage.data;
  const ratio = quotaBytes > 0 ? Math.min(1, usedBytes / quotaBytes) : 0;
  const text = t('settings.storageMgr.usage', { used: formatBytes(usedBytes, i18n.language), quota: formatBytes(quotaBytes, i18n.language) });
  return (
    <div className="flex max-w-lg flex-col gap-1.5 font-sans text-sm">
      <div
        role="meter"
        aria-label={t('settings.storageMgr.gauge')}
        aria-valuemin={0}
        aria-valuemax={quotaBytes}
        aria-valuenow={usedBytes}
        aria-valuetext={text}
        className="relative h-2 overflow-hidden rounded-full bg-surface-2"
      >
        <div className={cn('h-full rounded-full', ratio >= 0.8 ? 'bg-danger' : 'bg-accent')} style={{ width: `${ratio * 100}%` }} />
        <div className="absolute inset-y-0 w-px bg-fg-muted" style={{ left: '80%' }} aria-hidden="true" />
      </div>
      <p className="text-fg">{text}</p>
      <p className="text-xs text-fg-muted">{t('settings.storageMgr.gaugeHint')}</p>
    </div>
  );
}

/**
 * The board manager of the settings (P16-4 point 5): every board with what it stores, sorted by weight; search,
 * filter, selection; archive, trash, restore for everyone allowed, delete forever for admins (after naming the board).
 */
export function StorageSection({ org }: { org: Org }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const session = useSession();
  const undoable = useUndoable();
  const invalidate = useInvalidateContent();
  const report = useQuery({ queryKey: qk.storageReport(org.id), queryFn: () => api.storageReport(org.id) });
  const inbox = useInbox(org.id).data;
  const [show, setShow] = useState<Show>('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('total');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [purging, setPurging] = useState<StorageRow[] | null>(null);
  const [error, setError] = useState(false);
  const [lightening, setLightening] = useState(false);
  const userId = session.status === 'signed-in' ? session.user.id : '';
  const admin = org.role !== 'member';
  const size = (n: number) => formatBytes(n, i18n.language);

  const needle = q.trim().toLocaleLowerCase(i18n.language);
  // « Sans projet » (P17) is a place, not a project: the table says "No project", whatever name the server stores.
  const rows = (report.data ?? [])
    .map((r) => (r.projectId === inbox ? { ...r, projectName: t('dashboard.noProject') } : r))
    .filter((r) => shows(show, r) && (!needle || `${r.title} ${r.projectName}`.toLocaleLowerCase(i18n.language).includes(needle)))
    .sort((a, b) => {
      if (sort === 'title') return a.title.localeCompare(b.title, i18n.language);
      if (sort === 'project') return a.projectName.localeCompare(b.projectName, i18n.language) || a.title.localeCompare(b.title, i18n.language);
      if (sort === 'edited') return (b.lastEditedAt ?? '').localeCompare(a.lastEditedAt ?? '');
      return total(b) - total(a);
    });
  const chosen = rows.filter((r) => selected.has(r.boardId));
  // Trash and restore: admins, or the board's creator (the SQL rule of `lifecycle`).
  const mayTrash = (r: StorageRow) => canDelete(org.role, r.ownerId, userId);

  const run = async (action: LifecycleAction, list: StorageRow[]) => {
    setError(false);
    const { done } = await settle(
      list.map((r) => r.boardId),
      (id) => api.lifecycle('board', action, id),
    );
    if (done.length === 0) return setError(true);
    const inverse = INVERSE[action];
    const inverses: Inverse[] = inverse ? done.map((boardId) => ({ kind: 'lifecycle', boardId, action: inverse })) : [];
    await undoable(async () => {}, inverses, t(`settings.storageMgr.done.${action}`, { count: done.length }));
    setSelected(new Set());
  };

  const toggle = (id: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    setSelected(next);
  };

  const head = 'px-2 py-2 text-left font-sans text-xs font-medium text-fg-muted whitespace-nowrap';
  const cell = 'px-2 py-2 align-middle font-mono text-xs text-fg-muted whitespace-nowrap';
  const sortButton = (key: SortKey, label: string) => (
    <Button variant="ghost" size="sm" aria-pressed={sort === key} className="-mx-2 px-2 text-xs aria-pressed:text-fg" onClick={() => setSort(key)}>
      {label}
    </Button>
  );

  return (
    <Section title={t('settings.sections.storage')}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <Gauge org={org} />
        <Button onClick={() => setLightening(true)}>{t('settings.lighten.open')}</Button>
      </div>
      {lightening && <LightenDialog org={org} onClose={() => setLightening(false)} />}
      <div className="flex flex-wrap items-end gap-3">
        <Input type="search" aria-label={t('settings.storageMgr.search')} placeholder={t('settings.storageMgr.search')} className="max-w-xs" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="flex items-center gap-2 font-sans text-sm text-fg-muted">
          {t('settings.storageMgr.show')}
          <Select className="w-auto" value={show} onChange={(e) => (setShow(e.target.value as Show), setSelected(new Set()))}>
            {(['all', 'active', 'archived', 'trash'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`settings.storageMgr.shows.${s}`)}
              </option>
            ))}
          </Select>
        </label>
        {chosen.length > 0 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label={t('settings.storageMgr.selection')}>
            {show !== 'trash' && (
              <Button size="sm" onClick={() => void run('archive', chosen.filter((r) => !r.archivedAt))}>
                {t('settings.storageMgr.archiveN', { count: chosen.filter((r) => !r.archivedAt).length })}
              </Button>
            )}
            {show !== 'trash' && (
              <Button size="sm" onClick={() => void run('trash', chosen.filter(mayTrash))}>
                {t('settings.storageMgr.trashN', { count: chosen.filter(mayTrash).length })}
              </Button>
            )}
            {show === 'trash' && (
              <Button size="sm" onClick={() => void run('restore', chosen.filter(mayTrash))}>
                {t('settings.storageMgr.restoreN', { count: chosen.filter(mayTrash).length })}
              </Button>
            )}
            {show === 'trash' && admin && (
              <Button size="sm" variant="danger" onClick={() => setPurging(chosen)}>
                {t('settings.storageMgr.purgeN', { count: chosen.length })}
              </Button>
            )}
          </div>
        )}
      </div>
      {error && (
        <p role="alert" className="font-sans text-xs text-danger">
          {t('auth.genericError')}
        </p>
      )}
      <div className="overflow-x-auto rounded-lg bg-surface shadow-sm">
        <table aria-label={t('settings.storageMgr.table')} className="w-full border-collapse">
          <thead className="border-b border-border">
            <tr>
              <th className={head}>
                <span className="sr-only">{t('settings.storageMgr.selection')}</span>
              </th>
              <th className={head}>{sortButton('title', t('dashboard.listing.title'))}</th>
              <th className={head}>{sortButton('project', t('dashboard.project'))}</th>
              <th className={head}>{t('dashboard.listing.owner')}</th>
              <th className={head}>{t('settings.storageMgr.objects')}</th>
              <th className={head}>{t('settings.storageMgr.document')}</th>
              <th className={head}>{t('settings.storageMgr.images')}</th>
              <th className={head}>{t('settings.storageMgr.versions')}</th>
              <th className={head}>{sortButton('total', t('settings.storageMgr.total'))}</th>
              <th className={head}>{sortButton('edited', t('dashboard.listing.edited'))}</th>
              <th className={head}>
                <span className="sr-only">{t('settings.storageMgr.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.boardId} className="border-b border-border last:border-b-0">
                <td className={cell}>
                  <Checkbox hideLabel label={t('dashboard.select', { title: r.title })} checked={selected.has(r.boardId)} onCheckedChange={(on) => toggle(r.boardId, on)} />
                </td>
                <td className={cn(cell, 'max-w-60 truncate font-sans text-sm text-fg')} title={r.title}>
                  {r.deletedAt ? (
                    r.title
                  ) : (
                    <Link to={href('board', { boardId: r.boardId })} className={cn('text-fg hover:underline', focusRing)}>
                      {r.title}
                    </Link>
                  )}
                </td>
                <td className={cn(cell, 'max-w-40 truncate font-sans')} title={r.projectName}>
                  {r.projectName}
                </td>
                <td className={cn(cell, 'font-sans')}>{r.ownerName ?? '—'}</td>
                <td className={cell}>{r.objectCount}</td>
                <td className={cell}>{size(r.docBytes)}</td>
                <td className={cell}>
                  {size(r.imageBytes)} · {t('settings.storageMgr.imageCount', { count: r.imageCount })}
                  {r.sharedImageCount > 0 && ` · ${t('settings.storageMgr.shared', { count: r.sharedImageCount })}`}
                </td>
                <td className={cell}>
                  {size(r.versionBytes)} · {r.versionCount}
                </td>
                <td className={cn(cell, 'font-medium text-fg')}>{size(total(r))}</td>
                <td className={cell}>{r.lastEditedAt ? relativeTime(r.lastEditedAt, i18n.language) : '—'}</td>
                <td className={cn(cell, 'text-right')}>
                  <div className="flex justify-end gap-1">
                    {!r.deletedAt && (
                      <Button size="sm" variant="ghost" onClick={() => void run(r.archivedAt ? 'unarchive' : 'archive', [r])}>
                        {r.archivedAt ? t('dashboard.unarchive') : t('dashboard.archive')}
                      </Button>
                    )}
                    {!r.deletedAt && mayTrash(r) && (
                      <Button size="sm" variant="ghost" onClick={() => void run('trash', [r])}>
                        {t('dashboard.toTrash')}
                      </Button>
                    )}
                    {r.deletedAt && mayTrash(r) && (
                      <Button size="sm" variant="ghost" onClick={() => void run('restore', [r])}>
                        {t('dashboard.restore')}
                      </Button>
                    )}
                    {r.deletedAt && admin && (
                      <Button size="sm" variant="ghost" className="text-danger" onClick={() => setPurging([r])}>
                        {t('dashboard.purge')}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {report.isSuccess && rows.length === 0 && <p className="p-4 font-sans text-sm text-fg-muted">{t('settings.storageMgr.empty')}</p>}
      </div>
      {purging && (
        <Dialog
          title={purging.length === 1 ? t('settings.storageMgr.purgeOne', { title: purging[0]!.title }) : t('settings.storageMgr.purgeMany', { count: purging.length })}
          description={t('settings.storageMgr.purgeHint')}
          onClose={() => setPurging(null)}
          footer={
            <>
              <Button onClick={() => setPurging(null)}>{t('dashboard.cancel')}</Button>
              <Button
                variant="danger"
                onClick={async () => {
                  const list = purging;
                  setPurging(null);
                  const { done } = await settle(
                    list.map((r) => r.boardId),
                    (id) => api.lifecycle('board', 'purge', id),
                  );
                  if (done.length < list.length) setError(true);
                  await invalidate();
                  setSelected(new Set());
                }}
              >
                {t('dashboard.purge')}
              </Button>
            </>
          }
        />
      )}
    </Section>
  );
}
